import {
  type ComponentRef,
  type ComponentType,
  type ForwardedRef,
  forwardRef,
  type MutableRefObject,
  type RefCallback,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  type NativeScrollEvent,
  type NativeSyntheticEvent,
  ScrollView,
  type ScrollViewProps,
  type StyleProp,
  StyleSheet,
  type ViewProps,
  type ViewStyle,
} from 'react-native'
import { getCssTransform } from './css-transform'
import { getKeyframeFrameStyle, isKeyframe, type Keyframe, playWebKeyframeAnimation } from './keyframe'
import { getLayoutAnimationFrameStyles, getLayoutTransitionStyle } from './layout-animations'
import type { LayoutAnimationBuilder } from './types'

type LayoutAppearanceAnimation = LayoutAnimationBuilder | Keyframe

type AnimatedComponentProps = ViewProps & {
  entering?: LayoutAppearanceAnimation
  exiting?: LayoutAppearanceAnimation
  layout?: LayoutAnimationBuilder
}

const MOMENTUM_SCROLL_END_DELAY = 160
const ROUTE_ENTERING_SUPPRESSION_MS = 120
const EMPTY_STYLE: ViewStyle = {}

let lastLocationHref: string | undefined
let lastLocationChangeAt = 0

type WebTransitionViewStyle = ViewStyle & {
  transitionProperty?: string
  transitionDuration?: string
  transitionDelay?: string
  transitionTimingFunction?: string
}

type NodeWithGetter = {
  getNode?: () => unknown
}

type ParentPositionState = {
  count: number
  position: string
}

const exitingParentPositionStates = new WeakMap<HTMLElement, ParentPositionState>()

function isElement(value: unknown): value is HTMLElement {
  return typeof HTMLElement !== 'undefined' && value instanceof HTMLElement
}

function resolveElement(value: unknown): HTMLElement | null {
  if (isElement(value)) {
    return value
  }

  if (typeof value !== 'object' || value === null || !('getNode' in value)) {
    return null
  }

  const node = (value as NodeWithGetter).getNode?.()
  return isElement(node) ? node : null
}

function setForwardedRef<TElement>(ref: ForwardedRef<TElement>, value: TElement | null) {
  if (typeof ref === 'function') {
    ref(value)
    return
  }

  if (ref) {
    ref.current = value
  }
}

// Временно делает статический контейнер опорой для absolute-клона и учитывает параллельные exit-анимации.
function lockExitingParentPosition(parentElement: HTMLElement) {
  const positionState = exitingParentPositionStates.get(parentElement)

  if (positionState) {
    positionState.count += 1
    return true
  }

  const parentComputedStyle = window.getComputedStyle(parentElement)

  if (parentComputedStyle.position !== 'static') {
    return false
  }

  exitingParentPositionStates.set(parentElement, {
    count: 1,
    position: parentElement.style.position,
  })
  parentElement.style.position = 'relative'
  return true
}

// Возвращает контейнеру исходное позиционирование только после завершения всех связанных exit-анимаций.
function unlockExitingParentPosition(parentElement: HTMLElement) {
  const positionState = exitingParentPositionStates.get(parentElement)

  if (!positionState) {
    return
  }

  positionState.count -= 1

  if (positionState.count > 0) {
    return
  }

  exitingParentPositionStates.delete(parentElement)

  if (parentElement.style.position === 'relative') {
    parentElement.style.position = positionState.position
  }
}

function getTimestamp() {
  return window.performance?.now() ?? Date.now()
}

// Подавляет стартовую entering-анимацию сразу после смены маршрута, чтобы экран не дергался при переходе.
function shouldSuppressEnteringOnMount() {
  if (typeof window === 'undefined') {
    return false
  }

  const locationHref = window.location.href
  const now = getTimestamp()

  if (!lastLocationHref || lastLocationHref !== locationHref) {
    lastLocationHref = locationHref
    lastLocationChangeAt = now
    return true
  }

  return now - lastLocationChangeAt <= ROUTE_ENTERING_SUPPRESSION_MS
}

// Накладывает кадр layout-анимации на DOM-элемент и сохраняет базовый transform компонента.
function applyLayoutStyle(element: HTMLElement, style: ViewStyle, baseTransform?: string) {
  const transitionStyle = style as WebTransitionViewStyle

  if (typeof style.opacity === 'number') {
    element.style.opacity = String(style.opacity)
  }

  const transform = getCssTransform(style.transform)

  if (transform) {
    element.style.transform = baseTransform ? `${baseTransform} ${transform}` : transform
  }

  if (transitionStyle.transitionProperty) {
    element.style.transitionProperty = transitionStyle.transitionProperty
  }

  if (transitionStyle.transitionDuration) {
    element.style.transitionDuration = transitionStyle.transitionDuration
  }

  if (transitionStyle.transitionDelay) {
    element.style.transitionDelay = transitionStyle.transitionDelay
  }

  if (transitionStyle.transitionTimingFunction) {
    element.style.transitionTimingFunction = transitionStyle.transitionTimingFunction
  }
}

// Объединяет обычные стили с анимационными так, чтобы transform из компонента не затирался анимацией.
function mergeAnimatedStyle(style: StyleProp<ViewStyle>, animatedStyle: ViewStyle): StyleProp<ViewStyle> {
  const baseStyle = StyleSheet.flatten(style)

  if (!Array.isArray(baseStyle?.transform) || !Array.isArray(animatedStyle.transform)) {
    return [style, animatedStyle]
  }

  const animatedStyleWithoutTransform = { ...animatedStyle }
  delete animatedStyleWithoutTransform.transform

  return [
    style,
    {
      ...animatedStyleWithoutTransform,
      transform: [...baseStyle.transform, ...animatedStyle.transform],
    },
  ]
}

function getEnteringInitialStyle(entering: LayoutAppearanceAnimation | undefined): ViewStyle {
  if (!entering) {
    return EMPTY_STYLE
  }

  if (isKeyframe(entering)) {
    return getKeyframeFrameStyle(entering, 0)
  }

  return getLayoutAnimationFrameStyles(entering, 'enter').initialStyle
}

// Создает временный DOM-клон для exiting-анимации, пока исходный React-элемент уже размонтируется.
function animateExitingElement(element: HTMLElement | null, animation: LayoutAppearanceAnimation | undefined) {
  if (!element || !animation || typeof window === 'undefined') {
    animation?.callback?.(false)
    return
  }

  const parentElement = element.parentElement

  if (!parentElement) {
    animation.callback?.(false)
    return
  }

  const clone = element.cloneNode(true)

  if (!isElement(clone)) {
    animation.callback?.(false)
    return
  }

  const rect = element.getBoundingClientRect()
  const parentRect = parentElement.getBoundingClientRect()
  const computedStyle = window.getComputedStyle(element)

  if (rect.width === 0 && rect.height === 0) {
    animation.callback?.(false)
    return
  }

  const baseTransform = computedStyle.transform === 'none' ? undefined : computedStyle.transform
  const shouldRestoreParentPosition = lockExitingParentPosition(parentElement)
  const initialStyle = isKeyframe(animation)
    ? getKeyframeFrameStyle(animation, 0)
    : getLayoutAnimationFrameStyles(animation, 'exit').initialStyle

  clone.setAttribute('aria-hidden', 'true')
  clone.style.position = 'absolute'
  clone.style.pointerEvents = 'none'
  clone.style.boxSizing = 'border-box'
  clone.style.zIndex = computedStyle.zIndex
  clone.style.left = `${rect.left - parentRect.left - parentElement.clientLeft + parentElement.scrollLeft}px`
  clone.style.top = `${rect.top - parentRect.top - parentElement.clientTop + parentElement.scrollTop}px`
  clone.style.width = `${rect.width}px`
  clone.style.height = `${rect.height}px`
  clone.style.margin = '0'

  applyLayoutStyle(clone, initialStyle, baseTransform)
  parentElement.insertBefore(clone, element)

  if (isKeyframe(animation)) {
    const webAnimation = playWebKeyframeAnimation(clone, animation, baseTransform)

    window.setTimeout(() => {
      webAnimation?.cancel()
      clone.remove()

      if (shouldRestoreParentPosition) {
        unlockExitingParentPosition(parentElement)
      }

      animation.callback?.(true)
    }, animation.delayMs + animation.durationMs)
    return
  }

  const { targetStyle } = getLayoutAnimationFrameStyles(animation, 'exit')
  const frameId = window.requestAnimationFrame(() => {
    applyLayoutStyle(clone, targetStyle, baseTransform)
  })

  window.setTimeout(() => {
    window.cancelAnimationFrame(frameId)
    clone.remove()

    if (shouldRestoreParentPosition) {
      unlockExitingParentPosition(parentElement)
    }

    animation.callback?.(true)
  }, animation.delayMs + animation.durationMs)
}

// Хранит одноразовый entering-стиль и layout-transition отдельно, чтобы повторные рендеры не перезапускали вход.
function useAnimatedLayoutStyle(
  entering: LayoutAppearanceAnimation | undefined,
  layout: LayoutAnimationBuilder | undefined,
  elementRef: MutableRefObject<HTMLElement | null>,
): ViewStyle {
  const [shouldSuppressEntering] = useState(shouldSuppressEnteringOnMount)
  const enteringRef = useRef(shouldSuppressEntering ? undefined : entering)
  const [enteringStyle, setEnteringStyle] = useState<ViewStyle>(() =>
    shouldSuppressEntering ? EMPTY_STYLE : getEnteringInitialStyle(entering),
  )

  // Запускает entering-анимацию один раз после маунта и очищает временный стиль после завершения.
  useEffect(() => {
    const enteringAnimation = enteringRef.current

    if (!enteringAnimation || typeof window === 'undefined') {
      setEnteringStyle(EMPTY_STYLE)
      return undefined
    }

    if (isKeyframe(enteringAnimation)) {
      const element = elementRef.current

      if (!element) {
        setEnteringStyle(EMPTY_STYLE)
        return undefined
      }

      let webAnimation: Animation | null = null
      let timeoutId: ReturnType<typeof setTimeout> | null = null
      const frameId = window.requestAnimationFrame(() => {
        webAnimation = playWebKeyframeAnimation(element, enteringAnimation)
        setEnteringStyle(EMPTY_STYLE)
        timeoutId = setTimeout(() => {
          webAnimation?.cancel()
          enteringAnimation.callback?.(true)
        }, enteringAnimation.delayMs + enteringAnimation.durationMs)
      })

      return () => {
        window.cancelAnimationFrame(frameId)

        if (timeoutId) {
          clearTimeout(timeoutId)
        }

        webAnimation?.cancel()
      }
    }

    const { initialStyle, targetStyle } = getLayoutAnimationFrameStyles(enteringAnimation, 'enter')
    let timeoutId: ReturnType<typeof setTimeout> | null = null
    const frameId = window.requestAnimationFrame(() => {
      setEnteringStyle(targetStyle)
      timeoutId = setTimeout(() => {
        setEnteringStyle(EMPTY_STYLE)
        enteringAnimation.callback?.(true)
      }, enteringAnimation.delayMs + enteringAnimation.durationMs)
    })

    setEnteringStyle(initialStyle)

    return () => {
      window.cancelAnimationFrame(frameId)

      if (timeoutId) {
        clearTimeout(timeoutId)
      }
    }
  }, [])

  return useMemo(() => ({ ...getLayoutTransitionStyle(layout), ...enteringStyle }), [enteringStyle, layout])
}

function useExitingAnimation(exiting: LayoutAppearanceAnimation | undefined): RefCallback<HTMLElement> {
  const exitingRef = useRef(exiting)

  exitingRef.current = exiting

  return useCallback((element: HTMLElement | null) => {
    animateExitingElement(element, exitingRef.current)
  }, [])
}

// Сохраняет реальный DOM-элемент, чтобы запустить exiting-анимацию до окончательного сброса ref.
function useAnimatedElementRef<TElement>(
  ref: ForwardedRef<TElement>,
  onDetach: RefCallback<HTMLElement>,
): [MutableRefObject<HTMLElement | null>, RefCallback<TElement>] {
  const elementRef = useRef<HTMLElement | null>(null)
  // Обновляет внешний ref и запускает detach-логику только когда DOM-элемент действительно пропал.
  const setRef = useCallback(
    (value: TElement | null) => {
      const element = resolveElement(value)

      if (element) {
        elementRef.current = element
      } else if (elementRef.current) {
        onDetach(elementRef.current)
        elementRef.current = null
      }

      setForwardedRef(ref, value)
    },
    [onDetach, ref],
  )

  return [elementRef, setRef]
}

// Адаптирует ScrollView под web-анимации и синтетически завершает momentum-scroll при паузе скролла.
const AnimatedScrollView = forwardRef(function AnimatedScrollViewComponent(
  props: ScrollViewProps & AnimatedComponentProps,
  ref: ForwardedRef<ComponentRef<typeof ScrollView>>,
) {
  const { entering, exiting, layout, onScroll, onMomentumScrollEnd, scrollEventThrottle, style, ...rest } = props
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const lastEventRef = useRef<NativeSyntheticEvent<NativeScrollEvent> | null>(null)
  const runExitingAnimation = useExitingAnimation(exiting)
  const [elementRef, setRef] = useAnimatedElementRef(ref, runExitingAnimation)
  const animatedStyle = useAnimatedLayoutStyle(entering, layout, elementRef)
  const componentStyle = useMemo<StyleProp<ViewStyle>>(
    () => mergeAnimatedStyle(style, animatedStyle),
    [animatedStyle, style],
  )

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  // Прокидываем onScroll как есть и дебаунсом (пауза в скролле) синтезируем onMomentumScrollEnd.
  const handleScroll = (event: NativeSyntheticEvent<NativeScrollEvent>) => {
    onScroll?.(event)

    if (!onMomentumScrollEnd) return

    lastEventRef.current = event

    if (timeoutRef.current) clearTimeout(timeoutRef.current)

    timeoutRef.current = setTimeout(() => {
      timeoutRef.current = null

      if (lastEventRef.current) onMomentumScrollEnd(lastEventRef.current)
    }, MOMENTUM_SCROLL_END_DELAY)
  }

  return (
    <ScrollView
      ref={setRef}
      {...rest}
      scrollEventThrottle={scrollEventThrottle ?? 16}
      style={componentStyle}
      onScroll={handleScroll}
    />
  )
})

// Создает web-версию animated-компонента с поддержкой entering, exiting и layout-анимаций.
export function createAnimatedComponent<TProps extends ViewProps>(Component: ComponentType<TProps>) {
  if ((Component as unknown) === ScrollView) {
    return AnimatedScrollView as unknown as ComponentType<TProps & AnimatedComponentProps>
  }

  // Оборачивает обычный компонент в forwardRef, чтобы связать DOM-ref с web-реализацией анимаций.
  return forwardRef<unknown, TProps & AnimatedComponentProps>(function AnimatedComponent(props, ref) {
    const { entering, exiting, layout, style, ...rest } = props
    const runExitingAnimation = useExitingAnimation(exiting)
    const [elementRef, setRef] = useAnimatedElementRef(ref, runExitingAnimation)
    const animatedStyle = useAnimatedLayoutStyle(entering, layout, elementRef)
    const componentStyle = useMemo<StyleProp<ViewStyle>>(
      () => mergeAnimatedStyle(style, animatedStyle),
      [animatedStyle, style],
    )

    return <Component ref={setRef} {...({ ...rest, style: componentStyle } as unknown as TProps)} />
  })
}
